/**
 * @file Contributor UI — a3ProcessRawMaterials.
 */

export type A3ProcessRawMaterialRef = {
  id: number | null;
  name: string;
};

/** Stored in form state between multiple catalog raw-material ids. */
export const A3_PROCESS_RAW_MATERIAL_ID_DELIMITER = "|";

/** Exported function — serialize A3Process Raw Material Ids. */
export function serializeA3ProcessRawMaterialIds(
  ids: Array<string | number | null | undefined>
): string {
  return ids
    .map((id) => String(id ?? "").trim())
    .filter(Boolean)
    .join(A3_PROCESS_RAW_MATERIAL_ID_DELIMITER);
}

/** Exported function — parse A3Process Raw Material Ids. */
export function parseA3ProcessRawMaterialIds(raw: string): string[] {
  const text = String(raw ?? "").trim();
  if (!text) return [];
  return text
    .split(A3_PROCESS_RAW_MATERIAL_ID_DELIMITER)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Exported function — serialize A3Process Raw Material Names. */
export function serializeA3ProcessRawMaterialNames(names: string[]): string {
  return names.map((name) => name.trim()).filter(Boolean).join(", ");
}

/** Parse comma/semicolon/pipe-separated raw material names (bulk import + legacy rows). */
export function parseA3ProcessRawMaterialNames(raw: string): string[] {
  const text = String(raw ?? "").trim();
  if (!text) return [];
  return text
    .split(/[,;|]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Exported function — format A3Process Raw Materials Display. */
export function formatA3ProcessRawMaterialsDisplay(
  refs: A3ProcessRawMaterialRef[]
): string {
  return refs
    .map((ref) => ref.name.trim())
    .filter(Boolean)
    .join(", ");
}

/** Exported function — resolve A3Process Raw Material Refs From Form. */
export function resolveA3ProcessRawMaterialRefsFromForm(
  idsRaw: string,
  labelsRaw: string,
  options: Array<{ id: number; name: string }>
): A3ProcessRawMaterialRef[] {
  const ids = parseA3ProcessRawMaterialIds(idsRaw);
  const labels = parseA3ProcessRawMaterialNames(labelsRaw);
  const refs: A3ProcessRawMaterialRef[] = [];

  ids.forEach((idStr, index) => {
    const byId = options.find((o) => String(o.id) === idStr);
    const label = labels[index] ?? labels.find((_, i) => i === index) ?? "";
    refs.push({
      id: Number.isFinite(Number(idStr)) ? Number(idStr) : null,
      name: byId?.name ?? label ?? idStr,
    });
  });

  if (refs.length === 0 && labels.length > 0) {
    labels.forEach((name) => {
      const byName = options.find(
        (o) =>
          o.name === name || o.name.toLowerCase() === name.toLowerCase()
      );
      refs.push({
        id: byName?.id ?? null,
        name: byName?.name ?? name,
      });
    });
  }

  return refs;
}

/** Exported function — map A3Process Api Row To Raw Material Form Values. */
export function mapA3ProcessApiRowToRawMaterialFormValues(
  row: Record<string, unknown> | null | undefined,
  options: Array<{ id: number; name: string }>
): { ids: string; labels: string } {
  if (!row) return { ids: "", labels: "" };

  const json = row.rawMaterialsInvolved;
  if (json != null) {
    let parsed: unknown = json;
    if (typeof json === "string") {
      try {
        parsed = JSON.parse(json);
      } catch {
        parsed = null;
      }
    }
    if (Array.isArray(parsed) && parsed.length > 0) {
      const refs = parsed
        .map((item) => {
          if (!item || typeof item !== "object") return null;
          const rec = item as Record<string, unknown>;
          const idRaw = rec.id;
          const name = String(rec.name ?? "").trim();
          const id =
            idRaw != null && String(idRaw).trim() !== "" && Number.isFinite(Number(idRaw))
              ? Number(idRaw)
              : null;
          if (!name && id == null) return null;
          const byId =
            id != null ? options.find((o) => o.id === id) : undefined;
          return {
            id: byId?.id ?? id,
            name: byId?.name ?? name,
          } satisfies A3ProcessRawMaterialRef;
        })
        .filter(Boolean) as A3ProcessRawMaterialRef[];
      if (refs.length > 0) {
        return {
          ids: serializeA3ProcessRawMaterialIds(refs.map((r) => r.id)),
          labels: serializeA3ProcessRawMaterialNames(refs.map((r) => r.name)),
        };
      }
    }
  }

  const legacyText = String(
    row.description ?? row.rawMaterialName ?? row.processRawMaterials_A3_01 ?? ""
  ).trim();
  if (!legacyText) return { ids: "", labels: "" };

  const names = parseA3ProcessRawMaterialNames(legacyText);
  const refs = names.map((name) => {
    const byName = options.find(
      (o) => o.name === name || o.name.toLowerCase() === name.toLowerCase()
    );
    return { id: byName?.id ?? null, name: byName?.name ?? name };
  });

  return {
    ids: serializeA3ProcessRawMaterialIds(refs.map((r) => r.id)),
    labels: serializeA3ProcessRawMaterialNames(refs.map((r) => r.name)),
  };
}
